import { CurbZoneMapMode, CurbZonePageModeType } from "@/modules/curb-zone/curb-zone.type";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Dispatch, SetStateAction } from "react";
import { FeatureCollection, Polygon } from "geojson";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { WorkArea, WorkAreaProperties, formSchema } from "./work-area.type";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

interface ModifyWorkAreaPromptProps {
  appMode: CurbZoneMapMode;
  setOpenWorkAreaModal: () => void;
  setWorkAreaEditionData: Dispatch<SetStateAction<FeatureCollection<Polygon>>>;
  workAreaEditionData: FeatureCollection<Polygon>;
  workAreaDetail?: WorkArea | null;
  saveWorkAreaProperties: (promptedProperties: WorkAreaProperties) => Promise<void>;
  cancelWorkAreaChange: () => void;
}

export const ModifyWorkAreaPrompt = ({
  appMode,
  workAreaDetail,
  setOpenWorkAreaModal,
  workAreaEditionData,
  setWorkAreaEditionData,
  cancelWorkAreaChange,
  saveWorkAreaProperties,
}: ModifyWorkAreaPromptProps) => {
  const form = useForm<WorkAreaProperties>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: workAreaDetail?.name ?? "",
      done: workAreaDetail?.done ?? false,
      cityId: workAreaDetail?.cityId ?? "",
    },
    mode: "all",
  });

  const submit = async (formValues: WorkAreaProperties) => {
    await saveWorkAreaProperties(formValues);
    form.reset();
  };

  const handleClose = () => {
    if (appMode === CurbZonePageModeType.AddWorkArea) {
      setWorkAreaEditionData({ ...workAreaEditionData, features: workAreaEditionData.features.slice(0, -1) });
    }
    cancelWorkAreaChange();
    setOpenWorkAreaModal();
    form.reset();
  };

  return (
    <Dialog open>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{`${appMode === CurbZonePageModeType.AddWorkArea ? "Add a new" : "Edit a"} work area`}</DialogTitle>
          <DialogDescription>Work area information</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Name<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {appMode === CurbZonePageModeType.EditWorkArea && (
              <FormField
                control={form.control}
                name="done"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2">
                    <FormControl>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="work-area-status" checked={field.value} onCheckedChange={field.onChange} />
                        <label
                          htmlFor="work-area-status"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Done
                        </label>
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

            <DialogFooter className="sm:justify-between">
              <Button type="button" variant="secondary" onClick={handleClose}>
                Close
              </Button>
              <Button type="submit">Save</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
